import { useState } from "react";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { httpClient } from "@/httpClient";
import getPharmacyID from "@/utils/pharmacy_code";
import { CalendarIcon, X } from "lucide-react";
import { Select } from "@/components/ui/select";
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/custom-calendar";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useRouter } from "@tanstack/react-router";
import { pharmacyClient } from "@/connect";
dayjs.extend(customParseFormat);

const AddSupplier = ({
  buttonText = "Add Suppliers",
  buttonVariant = "default",
  buttonSize = "sm",
}: {
  buttonText?: string;
  buttonVariant?: "default" | "destructive" | "outline";
  buttonSize?: "sm" | "lg";
}) => {
  const { toast } = useToast();
  const creditPeriodOptions = [15, 30, 60, 90, 120, 180];
  const [supplier_name, set_supplier_name] = useState("");
  const [supplier_phone, set_supplier_phone] = useState("");
  const [contactPersonName, SetcontactPersonName] = useState("");
  const [creditPeriod, SetCreditPeriod] = useState("");
  const [monthlyPaymentDate, setMonthlyPaymentDate] = useState("");
  const [isCreditAllowed, SetIsCreditAllowed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [supplierCountry, setSupplierCountry] = useState("");
  const [supplierState, setSupplierState] = useState("");
  const [supplierCity, setSupplierCity] = useState("");
  const [supplierIsSEZ, setSupplierIsSEZ] = useState(false);
  const [supplierTaxIdentificationNumber, setSupplierTaxIdentificationNumber] = useState("");
  const router = useRouter();

  const SubmitSupplierDetails = async () => {
    setLoading(true);
    const creditPeriodInt = isCreditAllowed
      ? parseInt(creditPeriod, 10)
      : undefined;
    const monthlyPaymentDateInt = isCreditAllowed
      ? parseInt(monthlyPaymentDate, 10)
      : undefined;

    try {
      await pharmacyClient.createSupplier({
        supplier: {
          name: supplier_name,
          metadata: {
            phoneNumber: supplier_phone,
            contactPersonName: contactPersonName,
            isSez: supplierIsSEZ,
            country: supplierCountry,
            state: supplierState,
            city: supplierCity,
            taxIdentifier: {
              value: supplierTaxIdentificationNumber,
              type: pharmacyMetadata.countryCode === "IN" ? "GSTIN" : "TRN",
              description: pharmacyMetadata.countryCode === "IN" ? "GSTIN" : "Tax Registration Number",
            }
          },
          creditTerms: {
            isCreditAllowed: isCreditAllowed,
            creditPeriod: creditPeriodInt,
            monthlyPaymentDay: monthlyPaymentDateInt,
          }
        }
      })
      toast({
        title: "New Supplier Added",
        variant: "success",
        description: `Supplier ${supplier_name} has been added successfully`,
        duration: 5000,
      });
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        variant: "destructive",
        description: "Failed to add supplier",
        duration: 5000,
      });
    } finally {
      router.invalidate();
      set_supplier_name("");
      set_supplier_phone("");
      SetcontactPersonName("");
      SetCreditPeriod("");
      setMonthlyPaymentDate("");
      SetIsCreditAllowed(false);
      setSupplierCountry("");
      setSupplierState("");
      setSupplierCity("");
      setSupplierIsSEZ(false);
      setSupplierTaxIdentificationNumber("");
      setIsOpen(false);
      setLoading(false);
    }
  };

  const pharmacyMetadata = localStorage.getItem("pharmacyMetadata") ? JSON.parse(localStorage.getItem("pharmacyMetadata")!) : null;

  return (
    <>
      <Sheet open={isOpen}>
        <SheetTrigger asChild>
          <Button size={buttonSize} variant={buttonVariant} onClick={() => setIsOpen(!isOpen)}>{buttonText}</Button>
        </SheetTrigger>
        <SheetContent className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Add a new supplier</SheetTitle>
            <SheetDescription>
              The following details are required to add a new supplier
            </SheetDescription>
            <SheetClose
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-secondary"
              onClick={() => {
                set_supplier_name("");
                set_supplier_phone("");
                SetCreditPeriod("");
                setMonthlyPaymentDate("");
                SetIsCreditAllowed(false);
                setIsOpen(false);
                setSupplierCountry("");
                setSupplierState("");
                setSupplierCity("");
                setSupplierIsSEZ(false);
                setSupplierTaxIdentificationNumber("");
                SetcontactPersonName("");
              }}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </SheetClose>
          </SheetHeader>
          <p className="text-sm font-semibold mt-4 mb-2">Supplier Name</p>
          <Input
            placeholder="Supplier Name"
            value={supplier_name}
            onChange={(e) => set_supplier_name(e.target.value)}
          />
          <p className="text-sm font-semibold mt-4 mb-2">Contact Person Name</p>
          <Input
            placeholder="Contact Person Name"
            value={contactPersonName}
            onChange={(e) => SetcontactPersonName(e.target.value)}
          />
          <p className="text-sm font-semibold mt-4 mb-2">Phone Number</p>
          <Input
            placeholder="Phone Number"
            value={supplier_phone}
            type="number"
            onChange={(e) => set_supplier_phone(e.target.value)}
          />
          <p className="text-sm font-semibold mt-4 mb-2">Country</p>
          <Select onValueChange={(value) => {
            setSupplierCountry(value);
          }}>
            <SelectTrigger>
              <SelectValue placeholder="Select Country" />
            </SelectTrigger>
            <SelectContent>
              {pharmacyMetadata.countryCode === "IN" && <SelectItem value="IN">India</SelectItem>}
              {pharmacyMetadata.countryCode === "AE" && <SelectItem value="AE">United Arab Emirates</SelectItem>}
            </SelectContent>
          </Select>
          <p className="text-sm font-semibold mt-4 mb-2">{
            pharmacyMetadata.countryCode === "IN" ? "State" : "Emirate"
            }</p>
          <Select onValueChange={(value) => {
            setSupplierState(value);
          }}>
            <SelectTrigger>
              <SelectValue placeholder="Select State" />
            </SelectTrigger>
            <SelectContent>
              {pharmacyMetadata.countryCode === "IN" && (
                <>
                  <SelectItem value="AP">Andhra Pradesh</SelectItem>
                  <SelectItem value="AR">Arunachal Pradesh</SelectItem>
                  <SelectItem value="AS">Assam</SelectItem>
                  <SelectItem value="BR">Bihar</SelectItem>
                  <SelectItem value="CT">Chhattisgarh</SelectItem>
                  <SelectItem value="GA">Goa</SelectItem>
                  <SelectItem value="GJ">Gujarat</SelectItem>
                  <SelectItem value="HR">Haryana</SelectItem>
                  <SelectItem value="HP">Himachal Pradesh</SelectItem>
                  <SelectItem value="JK">Jammu and Kashmir</SelectItem>
                  <SelectItem value="JH">Jharkhand</SelectItem>
                  <SelectItem value="KA">Karnataka</SelectItem>
                  <SelectItem value="KL">Kerala</SelectItem>
                  <SelectItem value="MP">Madhya Pradesh</SelectItem>
                  <SelectItem value="MH">Maharashtra</SelectItem>
                  <SelectItem value="MN">Manipur</SelectItem>
                  <SelectItem value="ML">Meghalaya</SelectItem>
                  <SelectItem value="MZ">Mizoram</SelectItem>
                  <SelectItem value="NL">Nagaland</SelectItem>
                  <SelectItem value="OD">Odisha</SelectItem>
                  <SelectItem value="PB">Punjab</SelectItem>
                  <SelectItem value="RJ">Rajasthan</SelectItem>
                  <SelectItem value="SK">Sikkim</SelectItem>
                  <SelectItem value="TN">Tamil Nadu</SelectItem>
                  <SelectItem value="TG">Telangana</SelectItem>
                  <SelectItem value="TR">Tripura</SelectItem>
                  <SelectItem value="UP">Uttar Pradesh</SelectItem>
                  <SelectItem value="UK">Uttarakhand</SelectItem>
                  <SelectItem value="WB">West Bengal</SelectItem>
                  <SelectItem value="AN">Andaman and Nicobar Islands</SelectItem>
                  <SelectItem value="CH">Chandigarh</SelectItem>
                  <SelectItem value="DN">Dadra and Nagar Haveli and Daman and Diu</SelectItem>
                  <SelectItem value="DL">Delhi</SelectItem>
                  <SelectItem value="LD">Lakshadweep</SelectItem>
                  <SelectItem value="PY">Puducherry</SelectItem>
                  <SelectItem value="LA">Ladakh</SelectItem>                 
                </>
              )}
              {pharmacyMetadata.countryCode === "AE" && (
                <>
                  <SelectItem value="AZ">Abu Dhabi</SelectItem>
                  <SelectItem value="DU">Dubai</SelectItem>
                  <SelectItem value="SH">Sharjah</SelectItem>
                  <SelectItem value="AJ">Ajman</SelectItem>
                  <SelectItem value="RA">Ras Al Khaimah</SelectItem>
                  <SelectItem value="FU">Fujairah</SelectItem>
                  <SelectItem value="UM">Umm Al-Quwain</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
          <p className="text-sm font-semibold mt-4 mb-2">City</p>
          <Input
            placeholder="City"
            value={supplierCity}
            onChange={(e) => setSupplierCity(e.target.value)}
          />
          {/* SEZ is only applicable to India */}
          {pharmacyMetadata.countryCode === "IN" && (
            <div className="mt-4">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={supplierIsSEZ}
                  onChange={() => setSupplierIsSEZ(!supplierIsSEZ)}
                  className="form-checkbox"
                />
                <span className="ml-2">Is SEZ?</span>
              </label>
            </div>
          )}
          <p className="text-sm font-semibold mt-4 mb-2">{pharmacyMetadata.countryCode === "IN" ? "GSTIN" : "TRN"}</p>
          <Input
            placeholder={pharmacyMetadata.countryCode === "IN" ? "GSTIN" : "Tax Registration Number"}
            type="text"
            className="mb-4"
            value={supplierTaxIdentificationNumber}
            onChange={(e) => setSupplierTaxIdentificationNumber(e.target.value)}
          />
          <div className="mt-4">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={isCreditAllowed}
                onChange={() => SetIsCreditAllowed(!isCreditAllowed)}
                className="form-checkbox"
              />
              <span className="ml-2">Is Credit Allowed?</span>
            </label>
          </div>
          {isCreditAllowed && (
            <>
              <p className="text-sm font-semibold mt-4 mb-2">Credit Period</p>
              <Select onValueChange={(value) => SetCreditPeriod(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Credit Period" />
                </SelectTrigger>
                <SelectContent>
                  {creditPeriodOptions.map((option) => (
                    <SelectItem key={option} value={option.toString()}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm font-semibold mt-4 mb-2">
                Monthly Payment Date
              </p>
              <Select onValueChange={(value) => setMonthlyPaymentDate(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Day of Month" />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(31)].map((_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {i + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          <div className="flex justify-end">
            <Button
              className="mt-4"
              onClick={SubmitSupplierDetails}
              disabled={loading}
            >
              {loading ? "Loading..." : "Add Supplier"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default AddSupplier;